import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  STORAGE_PRESETS, STORAGE_WORM_LABEL, documentStorageLabel, documentStorageTarget,
  type DocumentStorageInput, type DocumentStorageTestResult, type DocumentStorageView,
} from '@pqrs/contracts';
import { Icono } from '@xiltrion/ui/icono';
import { Alert, AlertDescription, AlertTitle } from '@xiltrion/ui/alert';
import { Badge } from '@xiltrion/ui/badge';
import { Button } from '@xiltrion/ui/button';
import { ConfirmarDialogo } from '@xiltrion/ui/bloques/confirmar';
import { Plegable } from '@xiltrion/ui/bloques/plegable';
import { Spinner } from '@xiltrion/ui/spinner';
import { apiDelete, apiGet, apiPost, apiPut } from '@/api/client';
import { Card, ErrorState, LoadingState, PageHeader, fmtDate } from '@/components/ui';
import { errorMessage } from '@/features/cases/lib';
import { SETUP_QUERY_KEY } from '@/features/setup/api';
import { ConfiguredService, notify, notifyError } from './shared';
import { StorageBucketFields, bucketMissing, bucketSettingsOf, initialBucketForm, type BucketForm } from './StorageBucketFields';
import { BackupRestoreCard } from './BackupStoragePage';

const KEY = ['config', 'storage', 'documents'] as const;
export const useDocumentStorage = () => useQuery({ queryKey: KEY, queryFn: () => apiGet<DocumentStorageView>('/storage/documents') });

/** Sin llave escrita no se manda el campo (se conserva la guardada). */
function bodyOf(f: BucketForm): DocumentStorageInput {
  return { ...bucketSettingsOf(f), ...(f.secret ? { secretAccessKey: f.secret } : {}) };
}

/** Configuración › Plataforma › Almacenamiento. */
export function DocumentStoragePage() {
  return (
    <div>
      <PageHeader title="Almacenamiento de documentos" subtitle="Dónde quedan los archivos del expediente: originales, copias PDF/A, índices firmados, correos recibidos e imágenes de marca. Por defecto se usa el almacenamiento del servidor; la entidad puede traer su propio bucket compatible con S3." />
      <div className="space-y-6">
        <DocumentStorageCard />
        <BackupRestoreCard />
      </div>
    </div>
  );
}

/** Almacenamiento propio de la entidad: servicio, bucket, llaves y WORM. */
export function DocumentStorageCard() {
  const qc = useQueryClient();
  const q = useDocumentStorage();
  const [f, setF] = useState<BucketForm>(() => initialBucketForm(null));
  const [removing, setRemoving] = useState(false);
  /** Con bucket guardado se muestra el resumen; el formulario solo al editar. */
  const [editing, setEditing] = useState(false);
  const v = q.data;
  useEffect(() => { if (v) setF(initialBucketForm(v.settings)); }, [v?.updatedAt, v?.source]); // eslint-disable-line react-hooks/exhaustive-deps
  /** Con la suite conectada, el bucket lo asigna Datalogic allá (GUIA-AGENTES §3.12): aquí solo se ve y se prueba. */
  const managed = !!v?.managedBySuite;
  const summary = (v?.source === 'tenant' || v?.source === 'suite') && !editing;

  const set = (patch: Partial<BucketForm>) => { setF((x) => ({ ...x, ...patch })); test.reset(); };
  const saved = v?.settings ?? null;
  /** La llave guardada sirve solo si el destino es el mismo. */
  const storedSecretApplies = useMemo(() => {
    if (!saved?.hasSecret) return false;
    try { return documentStorageTarget(saved) === documentStorageTarget(bucketSettingsOf(f)); } catch { return false; }
  }, [saved, f]);
  const hasSecret = !!f.secret || storedSecretApplies;
  /** Con documentos guardados en el bucket de la entidad, el lugar no se cambia. */
  const hasDocuments = !!v && v.documentCount > 0;
  /** Con documentos en el bucket de la entidad, el lugar no se cambia (fuera de producción sí, para probar). */
  const locked = hasDocuments && v.source === 'tenant' && !v.development;
  /** Con documentos en el almacenamiento del servidor tampoco se pasa a un bucket propio (habría que migrarlos). */
  const cannotSwitch = hasDocuments && v.source === 'server' && !v.development;
  const docs = v ? `${v.documentCount} ${v.documentCount === 1 ? 'documento guardado' : 'documentos guardados'}` : '';

  const missing = bucketMissing(f, hasSecret, { retentionDays: true });

  const onSaved = (nv: DocumentStorageView, msg: string) => { qc.setQueryData(KEY, nv); qc.invalidateQueries({ queryKey: SETUP_QUERY_KEY }); qc.invalidateQueries({ queryKey: ['archive', 'signing'] }); notify('success', msg); };
  const save = useMutation({ mutationFn: () => apiPut<DocumentStorageView>('/storage/documents', bodyOf(f)), onSuccess: (nv) => { onSaved(nv, 'Almacenamiento guardado'); setEditing(false); }, onError: notifyError });
  const remove = useMutation({ mutationFn: () => apiDelete<DocumentStorageView>('/storage/documents'), onSuccess: (nv) => { onSaved(nv, 'Se vuelve al almacenamiento del servidor'); setEditing(false); }, onError: notifyError });
  /** En el resumen se prueba lo guardado (sin mandar configuración); al editar, lo del formulario. */
  const test = useMutation({ mutationFn: () => apiPost<DocumentStorageTestResult>('/storage/documents/test', summary ? {} : { config: bodyOf(f) }) });

  if (q.isLoading) return <Card title="Bucket de la entidad"><LoadingState rows={3} /></Card>;
  if (q.isError || !v) return <Card title="Bucket de la entidad"><ErrorState error={q.error} onRetry={() => q.refetch()} /></Card>;
  const blocked = v.secretsKey === 'missing';
  const cancelEdit = () => { setF(initialBucketForm(saved)); test.reset(); setEditing(false); };
  const wormMode = summary && saved ? saved.wormMode : f.wormMode;

  const wormStatus = (v.source === 'tenant' || v.source === 'suite') && saved && (
    <Alert variant={v.wormActive || saved.wormMode === 'off' ? 'success' : 'warning'}><Icono nombre={v.wormActive || saved.wormMode === 'off' ? 'mdi:database-check-outline' : 'mdi:database-alert-outline'} />
      <AlertTitle>Los documentos van al {documentStorageLabel(saved)}</AlertTitle>
      <AlertDescription>{saved.wormMode === 'off' ? 'Sin WORM.' : v.wormActive ? `WORM activo en modo ${saved.wormMode === 'compliance' ? 'cumplimiento' : 'gobernanza'}.` : 'El bucket no tiene Object Lock: el WORM configurado no aplica. Hay que crear el bucket con Object Lock habilitado.'}{locked ? ` Con ${docs}, el servicio, el bucket y la carpeta ya no se cambian; sí las llaves y el WORM.` : ''}</AlertDescription>
    </Alert>
  );
  const testResult = (<>
    {test.data && (test.data.ok
      ? <Alert variant="success"><Icono nombre="mdi:check-circle" /><AlertTitle>El bucket responde</AlertTitle><AlertDescription>{test.data.detail}{wormMode !== 'off' ? (test.data.wormActive ? ' El bucket tiene Object Lock: el WORM aplica.' : ' El bucket no tiene Object Lock: el WORM no aplicaría.') : ''}{test.data.warnings.map((w) => ` ${w}`).join('')}</AlertDescription></Alert>
      : <Alert variant="destructive"><Icono nombre="mdi:alert-circle-outline" /><AlertTitle>No funcionó</AlertTitle><AlertDescription className="break-words">{test.data.error}</AlertDescription></Alert>)}
    {test.isError && <Alert variant="destructive"><AlertDescription>{errorMessage(test.error)}</AlertDescription></Alert>}
  </>);
  const savedAt = (v.source === 'tenant' || v.source === 'suite') && v.updatedAt && <span className="text-xs text-muted-foreground ml-auto">{v.source === 'suite' ? 'Cambiado en la suite' : 'Guardado'} {fmtDate(v.updatedAt)}{v.updatedBy ? ` · ${v.updatedBy}` : ''}</span>;
  const removeButton = v.source === 'tenant' && !locked && !managed && <Button type="button" size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => setRemoving(true)}><Icono nombre="mdi:trash-can-outline" /> Volver al del servidor</Button>;
  const removeDialog = (
    <ConfirmarDialogo abierto={removing} onAbiertoChange={setRemoving} destructivo titulo="Volver al almacenamiento del servidor" textoConfirmar="Volver"
      descripcion={`Los documentos nuevos se guardarán en el ${v.server.label}. Las llaves guardadas se borran.`}
      onConfirmar={() => remove.mutateAsync().then(() => undefined)} />
  );

  const suiteNote = managed && (v.source === 'suite'
    ? <Alert variant="info"><Icono nombre="mdi:apps" /><AlertDescription>El bucket de los documentos lo asigna Datalogic en la suite (ficha de la entidad › Almacenamiento). Para cambiarlo, pídaselo a soporte.</AlertDescription></Alert>
    : v.source === 'tenant'
      ? <Alert variant="warning"><Icono nombre="mdi:swap-horizontal" /><AlertTitle>Aún se usa el que tenía SGDEA</AlertTitle><AlertDescription>La suite todavía no le asigna un bucket a SGDEA. Datalogic lo pasa allá con <code>pnpm suite:entity-export</code>; mientras tanto los documentos siguen yendo a este.</AlertDescription></Alert>
      : <Alert variant="info"><Icono nombre="mdi:apps" /><AlertTitle>Se usa el almacenamiento del servidor</AlertTitle><AlertDescription>Los documentos van al {v.server.label} mientras Datalogic no le asigne a SGDEA un bucket de la entidad en la suite (ficha de la entidad › Almacenamiento).</AlertDescription></Alert>);

  if (managed && !(summary && saved)) return <Card title="Bucket de la entidad">{suiteNote}</Card>;

  if (summary && saved) {
    const host = saved.endpoint ? saved.endpoint.replace(/^https?:\/\//, '') : `Amazon S3 (${saved.region})`;
    return (
      <Card title="Bucket de la entidad">
        <ConfiguredService icon="mdi:database-outline" title={<>Bucket {saved.bucket}{saved.prefix ? `/${saved.prefix}` : ''} en {STORAGE_PRESETS[saved.preset].label}</>}
          items={[
            { label: 'Servicio', value: host },
            { label: 'Región', value: saved.region },
            { label: 'Llave de acceso', value: saved.accessKeyId },
            { label: 'WORM', value: saved.wormMode === 'off' ? STORAGE_WORM_LABEL.off : <span className="inline-flex items-center gap-2">{STORAGE_WORM_LABEL[saved.wormMode]} · <span className="tnum">{saved.wormDefaultDays}</span> días provisionales <Badge variant={v.wormActive ? 'success-light' : 'warning-light'} size="sm">{v.wormActive ? 'Activo' : 'Sin Object Lock'}</Badge></span> },
            { label: 'Documentos', value: <span className="tnum">{v.documentCount}</span> },
          ]}
          actions={<>
            {!managed && <Button type="button" size="sm" variant="outline" onClick={() => { test.reset(); setEditing(true); }}><Icono nombre="mdi:pencil-outline" /> Editar</Button>}
            <Button type="button" size="sm" variant="outline" disabled={test.isPending} onClick={() => test.mutate()}>{test.isPending ? <Spinner /> : <Icono nombre="mdi:lan-connect" />} Probar conexión</Button>
            {removeButton}
            {savedAt}
          </>}>
          {suiteNote}
          {wormStatus}
          {testResult}
        </ConfiguredService>
        {removeDialog}
      </Card>
    );
  }

  return (
    <Card title="Bucket de la entidad">
      <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); if (!missing) save.mutate(); }}>
        {blocked && <Alert variant="destructive"><Icono nombre="mdi:key-outline" /><AlertTitle>El servidor no tiene SECRETS_KEY</AlertTitle><AlertDescription>Sin la llave maestra no se pueden guardar llaves de acceso. Hay que configurarla en el servidor.</AlertDescription></Alert>}
        {v.source === 'server' && (
          <Alert variant="info"><Icono nombre="mdi:server-outline" /><AlertTitle>Se usa el almacenamiento del servidor</AlertTitle>
            <AlertDescription>Hoy los documentos van al {v.server.label}{v.server.wormMode !== 'off' ? `, con WORM en modo ${v.server.wormMode === 'compliance' ? 'cumplimiento' : 'gobernanza'}` : ''}. {!hasDocuments ? 'Mientras no haya documentos, la entidad puede pasar a su propio bucket.' : v.development ? `Ya hay ${docs} allí.` : `Ya hay ${docs} allí: no se puede pasar a un bucket propio sin migrarlos.`}</AlertDescription>
          </Alert>
        )}
        {wormStatus}

        {hasDocuments && v.development && <Alert variant="warning"><Icono nombre="mdi:flask-outline" /><AlertTitle>Entorno de desarrollo</AlertTitle><AlertDescription>Se permite cambiar de almacén aunque haya {docs}. Los anteriores quedan en el almacén actual y no se podrán abrir desde el nuevo. En producción este cambio se rechaza.</AlertDescription></Alert>}
        <StorageBucketFields id="ds" f={f} set={set} locked={locked} storedSecretApplies={storedSecretApplies} retentionDays
          wormHint="Protege cada documento e índice firmado contra borrado o cambio hasta que venza su retención."
          requirements={(
            <Plegable titulo="Qué necesita el bucket">
              <ul className="text-sm text-muted-foreground m-0 pl-5 space-y-1">
                <li>Debe existir ya, en una cuenta de la entidad; la plataforma no lo crea. Un bucket dedicado, sin otros usos.</li>
                <li>Las llaves deben poder escribir, leer, borrar y listar en ese bucket, y nada más.</li>
                <li>Para WORM (escritura única) el bucket se crea con Object Lock habilitado; un bucket existente no se puede convertir. R2, Spaces, GCS y OCI no lo tienen.</li>
                <li>Una vez haya documentos, el bucket no se cambia: quedarían inaccesibles. Si hace falta migrar, se hace desde el servidor.</li>
              </ul>
            </Plegable>
          )} />

        {testResult}
        {missing && <p className="text-xs text-muted-foreground m-0">Falta {missing}.</p>}
        {!missing && cannotSwitch && <p className="text-xs text-destructive m-0">No se puede guardar: ya hay {docs} en el almacenamiento del servidor y quedarían inaccesibles en el bucket nuevo.</p>}

        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" size="sm" disabled={!!missing || save.isPending || blocked || cannotSwitch}>{save.isPending ? <Spinner /> : <Icono nombre="mdi:check" />} Guardar</Button>
          {editing && <Button type="button" size="sm" variant="ghost" onClick={cancelEdit}>Cancelar</Button>}
          <Button type="button" size="sm" variant="outline" disabled={!!missing || test.isPending} onClick={() => test.mutate()}>{test.isPending ? <Spinner /> : <Icono nombre="mdi:lan-connect" />} Probar conexión</Button>
          {removeButton}
          {savedAt}
        </div>
      </form>
      {removeDialog}
    </Card>
  );
}
